import { parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js/min";

export interface NormalizedPhone {
  /** E.164 form, e.g. +21625123456 */
  e164: string;
  /** National significant number, e.g. 25123456 */
  national: string;
  country: CountryCode | undefined;
  valid: boolean;
}

/**
 * Normalizes a phone typed by a customer ("25 123 456", "0021625123456", "+216 25-123-456") to E.164.
 * Returns null when nothing usable can be parsed.
 */
export function normalizePhone(raw: string | null | undefined, defaultCountry: CountryCode = "TN"): NormalizedPhone | null {
  if (!raw) return null;
  let input = raw.trim().replace(/[\s().-]/g, "");
  if (input.startsWith("00")) input = `+${input.slice(2)}`;
  if (!input) return null;
  const parsed = parsePhoneNumberFromString(input, defaultCountry);
  if (!parsed) return null;
  return {
    e164: parsed.number,
    national: parsed.nationalNumber,
    country: parsed.country,
    valid: parsed.isValid(),
  };
}

export function isValidPhone(raw: string | null | undefined, defaultCountry: CountryCode = "TN"): boolean {
  return normalizePhone(raw, defaultCountry)?.valid ?? false;
}

/** Display format used in the UI: "+216 25 123 456" for Tunisia, international format otherwise. */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return "";
  const parsed = parsePhoneNumberFromString(e164);
  if (!parsed) return e164;
  if (parsed.country === "TN") {
    const n = parsed.nationalNumber;
    return `+216 ${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5)}`;
  }
  return parsed.formatInternational();
}

/** Digits only, with country code, the format WhatsApp Cloud API and Meta CAPI expect before hashing. */
export function phoneDigits(e164: string): string {
  return e164.replace(/\D/g, "");
}
